import { html, LitElement, nothing, type PropertyValueMap } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { classMap } from 'lit/directives/class-map.js'

import style from './style/zaptic-checkbox-style'
import normalize from './style/normalize-style'

export enum Events {
    onChange = 'onChange',
}

export interface CustomEventPayload {
    [Events.onChange]: boolean
}

@customElement('zaptic-checkbox')
export class Checkbox extends LitElement {
    static styles = [normalize, style]

    @property({ type: Boolean })
    selected: boolean = false
    @property({ type: Boolean })
    disabled: boolean = false
    @property({ type: Boolean })
    hide: boolean = false
    @property({ type: Boolean })
    error: boolean = false
    @property({ type: Boolean })
    indeterminate: boolean = false
    @property()
    id: string
    @property()
    label: string
    @property()
    controlled: boolean = false /* to control checked via selected prop */

    @query('input')
    _checkbox: HTMLInputElement

    willUpdate(changedProperties: PropertyValueMap<Checkbox>) {
        if (changedProperties.has('indeterminate') && this._checkbox) {
            this._checkbox.indeterminate = this.indeterminate
        }
        if (this.controlled && changedProperties.has('selected') && this._checkbox) {
            this._checkbox.checked = this.selected
        }
    }

    firstUpdated() {
        this._checkbox.indeterminate = this.indeterminate
    }

    render() {
        return this.controlled
            ? html`
                  <label @click=${this.handleOnChange} class="controlled">
                      <input
                          class=${classMap({ error: this.error })}
                          id=${ifDefined(this.id || this.label)}
                          part="input"
                          type="checkbox"
                          .checked=${this.selected}
                          ?hidden=${this.hide}
                          ?disabled=${this.disabled}
                      />
                      <span>${this.label}</span>
                  </label>
              `
            : html`
                  <input
                      class=${classMap({ error: this.error })}
                      id=${ifDefined(this.id || this.label)}
                      part="input"
                      type="checkbox"
                      .checked=${this.selected}
                      ?hidden=${this.hide}
                      ?disabled=${this.disabled}
                      @change=${this.handleOnChange}
                  />
                  ${this.label ? html` <label for=${this.id || this.label}>${this.label}</label>` : nothing}
                  <slot name="label"></slot>
              `
    }

    private handleOnChange(e: Event) {
        this.controlled && e.preventDefault()
        this.dispatchEvent(
            new CustomEvent<CustomEventPayload['onChange']>(Events.onChange, {
                detail: !this.selected,
            })
        )
    }
}
